import * as API from '../../types/logging/core';

enum LogLevel {
    off = 0,
    error = 200,
    warn = 300,
    info = 400,
    debug = 500,
    trace = 600,
    all = Number.MAX_SAFE_INTEGER
}

const loggers: {[key: string]: Logger} = Object.create(null);
const logLevels: Record<string, LogLevel> = {};
export const ROOT_LOGGER_NAME = 'gateway';
logLevels[ROOT_LOGGER_NAME] = LogLevel.trace;
let appender : (e: API.LogEvent) => void  = (e) => {
    console[e.level](`${e.time.toISOString()} ${e.level.toUpperCase()} [${e.name}] - ${e.message}`, ...e.data);
};

type LogLevelKey = Exclude<keyof typeof LogLevel, 'off' | 'all'>;

export interface Logger extends API.Logger {
    (level: LogLevelKey, message?: string, ...args: unknown[])
}

export function logEvent(event: API.LogEvent) {
    const { name, level } = event;
    checkNameAndConfigureLevel(name);
    if (isLevelEnabledFor(level, name)) {
        appender(event);
    }
}

function log(this: Logger, name: string, level: LogLevelKey, message: string, ...data: unknown[]) {
    if (this.enabledFor(level)) {
        const time = new Date();
        const event: API.LogEvent = {time, level, name, message, data};
        appender(event);
    }
}

function isLevelEnabledFor(level: LogLevelKey, name: string) {
    const logLevelValue = LogLevel[level];
    const configuredLevel = logLevels[name];
    return configuredLevel >= logLevelValue;
}

function newLogger(name: string): Logger {
    checkNameAndConfigureLevel(name);
    const logger: Logger = function(level: LogLevelKey, message: string, ...args:unknown[]) {
        log.call(logger, name, level, message, ...args);
    } as Logger;
    function methods(key: string): key is LogLevelKey {
        return isNaN(Number(key));
    }
    for (const method of Object.keys(LogLevel).filter(methods)) {
        logger[method] = function(message: string, ...args: unknown[]) {
            log.call(logger, name, method, message, ...args);
        };
    }
    logger.enabledFor = function(level: LogLevelKey) {
        return isLevelEnabledFor(level, name);
    }
    logger.child = function (suffix: string) {
        return getLogger(`${name}.${suffix}`);
    }
    return logger;
}

function checkNameAndConfigureLevel(name: string) {
    if (!name.startsWith(ROOT_LOGGER_NAME)) throw new Error(`Logger name must start with ${ROOT_LOGGER_NAME}`);
    if (!logLevels[name]) {
        const orderedEntries = Object.entries(logLevels).sort(([n1,], [n2,])=> n2.localeCompare(n1));
        const [, level] = orderedEntries.find(([prefix,]) => name.startsWith(prefix))!;
        logLevels[name] = level;
    }
}

export function getLogger(name: string): API.Logger {
    let logger: Logger | undefined = loggers[name];
    if (logger === undefined) {
        logger = newLogger(name);
        loggers[name] = logger;
    }
    return logger;
}

export function configure(config: API.LogConfig) {
    function updateLevels(prefix: string, level: API.LogConfigLevel) {
        for (const name of Object.keys(logLevels).filter(k => k.startsWith(prefix))) {
            logLevels[name] = LogLevel[level];
        }
        if (logLevels[prefix] === undefined) {
            logLevels[prefix] = LogLevel[level];
        }
    }

    const logLevel = config.level;

    if (typeof logLevel === 'string') {
        logLevels[ROOT_LOGGER_NAME] = LogLevel[logLevel];
        updateLevels(ROOT_LOGGER_NAME, logLevel);
    }
    else if (typeof logLevel === 'object') {
        const orderedEntries = Object.entries(logLevel).sort(([n1], [n2]) => n1.localeCompare(n2));
        for (const [prefix, level] of orderedEntries) {
            updateLevels(prefix, level);
        }
    }

    appender = config.appender ?? appender;
}
